import * as React from "react";
import { PureComponent } from "react";
import {
  WithStyles,
  withStyles,
  Theme,
  createStyles,
  FormGroup,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  Tooltip,
  FormLabel,
  RadioGroup,
  Radio,
  Typography
} from "@material-ui/core";
import { validateContactInfo, checkForTypo } from "src/external/utilities/fieldValidator";
import { CommunicationPreferencesType, ContactPreferences } from "src/common/types/customerPrefs";
import { debounce } from "src/external/utilities/debounce";
import { CustomerInfo } from "src/external/types/customer";
import { Done } from "@material-ui/icons";
import PhoneNumberInput from "src/common/Components/general/PhoneNumberInput";

interface CustomerContactSettingsState {
  emailIsValid: boolean;
  phoneIsValid: boolean;
  suggestedEmail: string;
}

interface CustomerContactSettingsProps extends WithStyles<typeof styles> {
  customerInfo: CustomerInfo;
  preferences: ContactPreferences;
  margin?: "normal" | "none" | "dense" | undefined;
  isMemberExplicit?: boolean;
  phoneIsRequired?: boolean;
  isCustomer?: boolean;
  onContactSettingsChanged: (settings: ContactPreferences, isValid: boolean) => void;
  textIsDisabled?: boolean;
}

class CustomerContactSettings extends PureComponent<CustomerContactSettingsProps, CustomerContactSettingsState> {
  static getDerivedStateFromProps(nextProps: CustomerContactSettingsProps) {
    const validation = validateContactInfo(
      nextProps.preferences.communicationPreference,
      nextProps.preferences.phone,
      nextProps.preferences.email
    );
    return {
      emailIsValid: validation.validEmail,
      phoneIsValid: validation.validPhone
    };
  }

  private debounceEmailCheck: () => void;

  constructor(props: CustomerContactSettingsProps) {
    super(props);

    const validation = validateContactInfo(
      props.preferences.communicationPreference,
      props.preferences.phone,
      props.preferences.email
    );
    props.onContactSettingsChanged(props.preferences, validation.validEmail && validation.validPhone);

    this.state = {
      emailIsValid: validation.validEmail,
      phoneIsValid: validation.validPhone,
      suggestedEmail: ""
    };

    this.debounceEmailCheck = debounce(() => {
      const emailIsValid = this.state.emailIsValid;
      if (emailIsValid) {
        const suggestedEmail = checkForTypo(
          this.props.customerInfo.firstName,
          this.props.customerInfo.lastName,
          this.props.preferences.email
        );
        if (suggestedEmail) {
          this.setState({ suggestedEmail, emailIsValid });
        } else {
          this.setState({ suggestedEmail: "", emailIsValid });
        }
      } else {
        this.setState({
          suggestedEmail: ""
        });
      }
    }, 500);
  }

  handleIsMobileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isMobilePhone = event.target.checked;
    const communicationPreference: CommunicationPreferencesType = !isMobilePhone
      ? "EMAIL"
      : this.props.preferences.communicationPreference;

    const validation = validateContactInfo(
      communicationPreference,
      this.props.preferences.phone,
      this.props.preferences.email
    );

    this.props.onContactSettingsChanged(
      { ...this.props.preferences, isMobilePhone: event.target.checked },
      validation.validEmail && validation.validPhone
    );
  };

  handlePhoneNumberChange = (phoneNumber: React.ChangeEvent<HTMLInputElement>) => {
    const validation = validateContactInfo(
      this.props.preferences.communicationPreference,
      phoneNumber.target.value,
      this.props.preferences.email
    );

    this.props.onContactSettingsChanged(
      { ...this.props.preferences, phone: phoneNumber.target.value },
      validation.validEmail && validation.validPhone
    );
  };

  handlePreferredContactMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (this.props.textIsDisabled) {
      if ((event.target.value as CommunicationPreferencesType) == "TEXT") {
        return;
      }
    }
    const validation = validateContactInfo(
      event.target.value as CommunicationPreferencesType,
      this.props.preferences.phone,
      this.props.preferences.email
    );

    this.props.onContactSettingsChanged(
      { ...this.props.preferences, communicationPreference: event.target.value as CommunicationPreferencesType },
      validation.validEmail && validation.validPhone
    );
  };

  handleEmailChange = (email: React.ChangeEvent<HTMLInputElement>) => {
    const validation = validateContactInfo(
      this.props.preferences.communicationPreference,
      this.props.preferences.phone,
      email.target.value
    );

    this.setState({ emailIsValid: validation.validEmail }, () => {
      this.debounceEmailCheck();
    });

    this.props.onContactSettingsChanged(
      { ...this.props.preferences, email: email.target.value },
      validation.validEmail && validation.validPhone
    );
  };

  replaceEmail = () => {
    const sg = this.state.suggestedEmail;
    this.setState({
      suggestedEmail: ""
    });
    this.props.onContactSettingsChanged(
      { ...this.props.preferences, email: sg },
      this.state.emailIsValid && this.state.phoneIsValid
    );
  };

  handlePrefix = () => {
    if (this.props.isMemberExplicit) {
      return "Visitor's ";
    }
    return "";
  };

  buildRadioButton(pref: CommunicationPreferencesType, label: string, textingDisabled?: boolean) {
    const radioButton = (
      <FormControlLabel
        value={pref.toString()}
        control={
          textingDisabled ? (
            <Tooltip title={"Texting is disabled"} placement={"left"}>
              <Radio color={"primary"} className={textingDisabled ? this.props.classes.disabled : ""} />
            </Tooltip>
          ) : (
            <Radio color={"primary"} />
          )
        }
        label={
          textingDisabled ? (
            <Typography className={textingDisabled == true ? this.props.classes.disabled : ""}>{label}</Typography>
          ) : (
            <Typography>{label}</Typography>
          )
        }
      />
    );

    return radioButton;
  }

  render() {
    const { classes, textIsDisabled } = this.props;
    const { emailIsValid, phoneIsValid } = this.state;
    const { margin } = this.props;
    const { communicationPreference, email, phone, isMobilePhone } = this.props.preferences;

    const prefix = this.handlePrefix();
    const phoneText = "Contact Number";
    const emailText = "Contact Email";

    return (
      <>
        <FormGroup className={classes.contactNumberContainer}>
          <TextField
            id="phoneNumber"
            label={communicationPreference === "TEXT" ? prefix + phoneText + " *" : prefix + phoneText}
            value={phone}
            margin={margin || "normal"}
            onChange={this.handlePhoneNumberChange}
            error={!phoneIsValid}
            className={classes.contactNumberInput}
            InputProps={{
              inputComponent: PhoneNumberInput
            }}
          />
          <FormControlLabel
            control={<Checkbox color="primary" checked={isMobilePhone} onChange={this.handleIsMobileChange} />}
            label="Mobile?"
            className={classes.contactNumberCheck}
          />
        </FormGroup>
        <TextField
          id="email"
          label={communicationPreference === "EMAIL" ? prefix + emailText + " *" : prefix + emailText}
          value={email.trim()}
          margin={margin || "normal"}
          onChange={this.handleEmailChange}
          error={communicationPreference === "EMAIL" && !emailIsValid}
        />
        {this.state.suggestedEmail && (
          <Chip
            style={{ width: "fit-content", marginBottom: "0.5rem" }}
            label={`Did you mean ${this.state.suggestedEmail}`}
            onDelete={this.replaceEmail}
            variant="outlined"
            color="primary"
            deleteIcon={<Done />}
          />
        )}
        <FormControl margin="normal">
          <FormLabel component="label">Preferred Method Of Contact</FormLabel>
          <RadioGroup value={communicationPreference} onChange={this.handlePreferredContactMethodChange}>
            {this.buildRadioButton("EMAIL", "Email")}
            {this.buildRadioButton("TEXT", "Text", textIsDisabled)}
          </RadioGroup>
        </FormControl>
      </>
    );
  }
}
const styles = (theme: Theme) =>
  createStyles({
    contactNumberContainer: {
      display: "flex",
      flexDirection: "row"
    },
    contactNumberInput: {
      flexGrow: 1
    },
    contactNumberCheck: {
      alignSelf: "flex-end"
    },
    menu: {
      padding: 0
    },
    menuItems: {
      borderRadius: 0,
      boxShadow: "none",
      marginTop: 0
    },
    disabled: {
      color: `rgba(0, 0, 0, 0.26)`,
      cursor: "default"
    }
  });

export default withStyles(styles)(CustomerContactSettings);
